import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, BehaviorSubject } from "rxjs";
import { AuthService } from "../services/auth.service";

// Interfaces for User App API
export interface UserApp {
  appId: string;
  name: string;
  category: string;
  icon: string;
  addedAt: string;
}

export interface UserAppResponse {
  count: number;
  items: UserApp[];
}

export interface AddUserAppRequest {
  appId: string;
}

export interface RemoveUserAppRequest {
  appId: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserAppService {
  private readonly apiUrl = 'https://w0dkbhqy89.execute-api.us-east-1.amazonaws.com/Stage/user_app/user';
  
  // Reactive state management
  private userAppsSubject = new BehaviorSubject<UserApp[]>([]);
  public userApps$ = this.userAppsSubject.asObservable();

  constructor(
    private _httpClient: HttpClient,
    private _authService: AuthService
  ) {}

  // Get user's apps
  getUserApps(): Observable<UserAppResponse> {
    return new Observable(observer => {
      this._authService.getUser().then(user => {
        const userId = user?.sub || 'unknown';
        this._httpClient.get<UserAppResponse>(`${this.apiUrl}?user_id=${userId}`, this.getHttpOptions()).subscribe({
          next: (result) => {
            // Update the BehaviorSubject
            this.userAppsSubject.next(result.items || []);
            observer.next(result);
            observer.complete();
          },
          error: (error) => {
            observer.error(error);
          }
        });
      }).catch(error => {
        observer.error(error);
      });
    });
  }

  // Add app to user's collection
  addUserApp(appId: string): Observable<any> {
    return new Observable(observer => {
      this._authService.getUser().then(user => {
        const userId = user?.sub || 'unknown';
        const requestBody: AddUserAppRequest = { appId };
        
        this._httpClient.post<any>(`${this.apiUrl}?user_id=${userId}`, requestBody, this.getHttpOptions()).subscribe({
          next: (result) => {
            // Refresh the user apps list
            this.refreshUserApps();
            observer.next(result);
            observer.complete();
          },
          error: (error) => {
            observer.error(error);
          }
        });
      }).catch(error => {
        observer.error(error);
      });
    });
  }

  // Remove app from user's collection
  removeUserApp(appId: string): Observable<any> {
    return new Observable(observer => {
      this._authService.getUser().then(user => {
        const userId = user?.sub || 'unknown';
        
        this._httpClient.delete<any>(`${this.apiUrl}/${appId}?user_id=${userId}`, this.getHttpOptions()).subscribe({
          next: (result) => {
            // Refresh the user apps list
            this.refreshUserApps();
            observer.next(result);
            observer.complete();
          },
          error: (error) => {
            observer.error(error);
          }
        });
      }).catch(error => {
        observer.error(error);
      });
    });
  }

  // Get current user apps from the BehaviorSubject
  getCurrentUserApps(): UserApp[] {
    return this.userAppsSubject.value;
  }

  // Refresh user apps and update the BehaviorSubject
  private refreshUserApps(): void {
    this.getUserApps().subscribe({
      next: (result) => {
        // BehaviorSubject is already updated in getUserApps
      },
      error: (error) => {
        console.error('Error refreshing user apps:', error);
      }
    });
  }

  // HTTP options with authentication
  private getHttpOptions() {
    return {
      headers: new HttpHeaders({
        'Authorization': `Bearer ${this._authService.accessToken}`,
        'Content-Type': 'application/json'
      })
    };
  }
}
